(() => {
  'use strict';
  const storageKey = 'aveyra:favourites:v1';
  let favourites = [];
  const validHandle = (handle) => typeof handle === 'string' && handle.length > 0 && handle.length <= 200 && !/[/?#\s]/.test(handle);
  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter(validHandle))] : [];
    } catch (_) {
      return favourites;
    }
  };
  favourites = read();
  const page = document.querySelector('[data-aveyra-favourites]');
  const grid = page?.querySelector('[data-wishlist-grid]');
  const pending = new Set();
  const failed = new Set();

  function hydrate() {
    document.querySelectorAll('.header__icon--wishlist').forEach((icon) => {
      let badge = icon.querySelector('.aveyra-wishlist-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'aveyra-wishlist-count';
        badge.setAttribute('aria-hidden', 'true');
        icon.querySelector('.svg-wrapper').append(badge);
      }
      const count = favourites.length;
      const previousCount = Number(icon.dataset.wishlistCount || 0);
      badge.textContent = count > 9 ? '9+' : String(count);
      icon.dataset.wishlistCount = String(count);
      icon.setAttribute('data-wishlist-has-items', String(count > 0));
      if (previousCount > 0 && count > 0 && previousCount !== count && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        badge.getAnimations().forEach((animation) => animation.cancel());
        badge.animate([
          { opacity: 0.65, transform: 'scale(0.9)' },
          { opacity: 1, transform: 'scale(1)' }
        ], { duration: 180, easing: 'ease' });
      }
    });
    document.querySelectorAll('[data-aveyra-wishlist]').forEach((button) => {
      const selected = favourites.includes(button.dataset.aveyraWishlist);
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', `${selected ? button.dataset.labelRemove : button.dataset.labelAdd}: ${button.dataset.productTitle}`);
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = selected ? button.dataset.labelSelected : button.dataset.labelAdd;
    });
  }

  function updateStatus() {
    if (!page) return;
    page.querySelector('[data-wishlist-empty]').hidden = favourites.length !== 0;
    page.querySelector('[data-wishlist-status]').hidden = !favourites.some((handle) => pending.has(handle));
    page.querySelector('[data-wishlist-error]').hidden = !favourites.some((handle) => failed.has(handle));
    grid.setAttribute('aria-busy', String(favourites.some((handle) => pending.has(handle))));
  }

  function removeCard(item) {
    const handle = item.dataset.wishlistItem;
    // Dawn moves Quick Add dialogs to body. Remove the matching dialog too.
    document.querySelectorAll('quick-add-modal[data-wishlist-owner]').forEach((modal) => {
      if (modal.dataset.wishlistOwner === handle) {
        if (modal.hasAttribute('open')) modal.hide();
        modal.remove();
      }
    });
    item.remove();
  }

  async function loadCard(handle) {
    pending.add(handle);
    failed.delete(handle);
    updateStatus();
    try {
      const root = page.dataset.productsRoot.replace(/\/?$/, '/');
      const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set('view', 'wishlist-card');
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Wishlist product unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const item = html.querySelector('[data-wishlist-item]');
      if (!item || item.dataset.wishlistItem !== handle) throw new Error('Wishlist card unavailable');
      if (!favourites.includes(handle)) return;
      item.querySelectorAll('quick-add-modal').forEach((modal) => {
        modal.dataset.wishlistOwner = handle;
      });
      const position = favourites.indexOf(handle);
      const next = Array.from(grid.children).find((card) => favourites.indexOf(card.dataset.wishlistItem) > position);
      grid.insertBefore(item, next || null);
      hydrate();
    } catch (_) {
      failed.add(handle);
    } finally {
      pending.delete(handle);
      updateStatus();
    }
  }

  function reconcile() {
    hydrate();
    document.dispatchEvent(new CustomEvent('aveyra:wishlist-change', { detail: [...favourites] }));
    if (!page) return;
    Array.from(grid.children).forEach((item) => {
      if (!favourites.includes(item.dataset.wishlistItem)) removeCard(item);
    });
    const rendered = new Set(Array.from(grid.children).map((item) => item.dataset.wishlistItem));
    favourites.forEach((handle) => {
      if (!rendered.has(handle) && !pending.has(handle) && !failed.has(handle)) loadCard(handle);
    });
    updateStatus();
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-aveyra-wishlist]');
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      const handle = button.dataset.aveyraWishlist;
      if (!validHandle(handle)) return;
      // Merge changes made in another tab before writing our toggle.
      favourites = read();
      favourites = favourites.includes(handle) ? favourites.filter((saved) => saved !== handle) : [...favourites, handle];
      try { localStorage.setItem(storageKey, JSON.stringify(favourites)); } catch (_) { /* Keep working for this visit if storage is unavailable. */ }
      failed.delete(handle);
      const hadFocus = button === document.activeElement;
      reconcile();
      if (hadFocus && !button.isConnected) {
        const next = grid?.querySelector('[data-aveyra-wishlist]') || page?.querySelector('[data-wishlist-empty] a');
        next?.focus();
      }
    } else if (event.target.closest('[data-wishlist-retry]')) {
      failed.clear();
      reconcile();
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === null) {
      favourites = read();
      failed.clear();
      reconcile();
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    favourites = read();
    failed.clear();
    reconcile();
  });

  // Facet AJAX, recommendations and variant updates insert fresh native cards.
  // Observe child additions only; our own aria/state changes do not trigger it.
  let scheduled = false;
  new MutationObserver((records) => {
    if (scheduled || !records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1))) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      hydrate();
    });
  }).observe(document.body, { childList: true, subtree: true });
  reconcile();
})();
